import React, { useState, useEffect } from 'react';
import Button from '../UI/Button';
import { animalsAPI } from '../../services/api';

// Romanian counties for dropdown
const romanianCounties = [
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani', 'Brăila',
  'Brașov', 'Bucharest', 'Buzău', 'Călărași', 'Caraș-Severin', 'Cluj', 'Constanța',
  'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj', 'Harghita', 'Hunedoara',
  'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 'Mehedinți', 'Mureș', 'Neamț', 'Olt',
  'Prahova', 'Sălaj', 'Satu Mare', 'Sibiu', 'Suceava', 'Teleorman', 'Timiș', 'Tulcea',
  'Vâlcea', 'Vaslui', 'Vrancea'
];

interface Animal {
  id: string;
  name: string;
  age: string;
  gender: string;
  color: string;
  status: string;
}

interface AdoptionApplication {
  // Animal information
  animalId: string;
  animalName: string;
  animalAge: string;
  animalGender: string;
  
  // Adopter information
  adopterFirstName: string;
  adopterLastName: string;
  adopterEmail: string;
  adopterPhone: string;
  adopterAddress: string;
  adopterCity: string;
  adopterCounty: string;
  adopterPostalCode: string;
  
  // Housing information
  housingType: string;
  hasYard: boolean;
  yardSize: string;
  hasOtherPets: boolean;
  otherPetsDetails: string;
  hasChildren: boolean;
  childrenAges: string;
  
  // Experience and motivation
  previousPetExperience: string;
  adoptionReason: string;
  timeAvailable: string;
  
  // References
  applicant_references: Array<{
    name: string;
    phone: string;
    relationship: string;
  }>;
  
  // Administrative
  applicationDate: string;
  status: 'pending' | 'approved' | 'rejected';
  homeVisitScheduled: boolean;
  homeVisitDate?: string;
  contractSigned: boolean;
  notes: string;
}

interface AdoptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (adoption: Partial<AdoptionApplication>) => void;
  adoption?: AdoptionApplication | null;
  mode: 'add' | 'edit';
}

const AdoptionModal: React.FC<AdoptionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  adoption,
  mode
}) => {
  const [formData, setFormData] = useState({
    animalId: '',
    animalName: '',
    animalAge: '',
    animalGender: '',
    adopterFirstName: '',
    adopterLastName: '',
    adopterEmail: '',
    adopterPhone: '',
    adopterAddress: '',
    adopterCity: '',
    adopterCounty: '',
    adopterPostalCode: '',
    housingType: '',
    hasYard: false,
    yardSize: '',
    hasOtherPets: false,
    otherPetsDetails: '',
    hasChildren: false,
    childrenAges: '',
    previousPetExperience: '',
    adoptionReason: '',
    timeAvailable: '',
    applicant_references: [{ name: '', phone: '', relationship: '' }],
    homeVisitScheduled: false,
    homeVisitDate: '',
    status: 'pending' as 'pending' | 'approved' | 'rejected',
    notes: '',
    contractSigned: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Available animals - will be loaded from API
  const [availableAnimals, setAvailableAnimals] = useState<Animal[]>([]);
  const [filteredAnimals, setFilteredAnimals] = useState<Animal[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Helper function to convert ISO date to yyyy-MM-dd format
  const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    return dateString.split('T')[0]; // Extract just the date part
  };

  useEffect(() => {
    // Load available animals from API
    const loadAnimals = async () => {
      try {
        const response = await animalsAPI.getAnimals();
        const animals = response.data?.animals || response.data || response;
        // Filter only available animals
        const availableOnly = animals.filter((animal: any) => animal.status === 'available');
        setAvailableAnimals(availableOnly);
        setFilteredAnimals(availableOnly);
        console.log('Loaded available animals:', availableOnly.length);
      } catch (error) {
        console.error('Error loading animals:', error);
        // Show user-friendly error message
        if (error instanceof Error && error.message && error.message.includes('token')) {
          console.error('Authentication required. Please login first.');
        }
      }
    };

    if (isOpen) {
      loadAnimals();
    }
  }, [isOpen]);

  // Filter animals based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = availableAnimals.filter(animal =>
        animal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        animal.color.toLowerCase().includes(searchTerm.toLowerCase()) ||
        animal.gender.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAnimals(filtered);
    } else {
      setFilteredAnimals(availableAnimals);
    }
  }, [searchTerm, availableAnimals]);

  useEffect(() => {
    if (adoption && mode === 'edit') {
      setFormData({
        animalId: adoption.animalId || '',
        animalName: adoption.animalName || '',
        animalAge: adoption.animalAge || '',
        animalGender: adoption.animalGender || '',
        adopterFirstName: adoption.adopterFirstName || '',
        adopterLastName: adoption.adopterLastName || '',
        adopterEmail: adoption.adopterEmail || '',
        adopterPhone: adoption.adopterPhone || '',
        adopterAddress: adoption.adopterAddress || '',
        adopterCity: adoption.adopterCity || '',
        adopterCounty: adoption.adopterCounty || '',
        adopterPostalCode: adoption.adopterPostalCode || '',
        housingType: adoption.housingType || '',
        hasYard: adoption.hasYard || false,
        yardSize: adoption.yardSize || '',
        hasOtherPets: adoption.hasOtherPets || false,
        otherPetsDetails: adoption.otherPetsDetails || '',
        hasChildren: adoption.hasChildren || false,
        childrenAges: adoption.childrenAges || '',
        previousPetExperience: adoption.previousPetExperience || '',
        adoptionReason: adoption.adoptionReason || '',
        timeAvailable: adoption.timeAvailable || '',
        applicant_references: adoption.applicant_references || [{ name: '', phone: '', relationship: '' }],
        homeVisitScheduled: adoption.homeVisitScheduled || false,
        homeVisitDate: formatDateForInput(adoption.homeVisitDate),
        status: adoption.status || 'pending',
        notes: adoption.notes || '',
        contractSigned: adoption.contractSigned || false
      });
    } else if (mode === 'add') {
      setFormData({
        animalId: '',
        animalName: '',
        animalAge: '',
        animalGender: '',
        adopterFirstName: '',
        adopterLastName: '',
        adopterEmail: '',
        adopterPhone: '',
        adopterAddress: '',
        adopterCity: '',
        adopterCounty: '',
        adopterPostalCode: '',
        housingType: '',
        hasYard: false,
        yardSize: '',
        hasOtherPets: false,
        otherPetsDetails: '',
        hasChildren: false,
        childrenAges: '',
        previousPetExperience: '',
        adoptionReason: '',
        timeAvailable: '',
        applicant_references: [{ name: '', phone: '', relationship: '' }],
        homeVisitScheduled: false,
        homeVisitDate: '',
        status: 'pending',
        notes: '',
        contractSigned: false
      });
    }
  }, [adoption, mode, isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleAnimalSelect = (animalId: string) => {
    const selectedAnimal = availableAnimals.find(animal => animal.id === animalId);
    if (selectedAnimal) {
      setFormData(prev => ({
        ...prev,
        animalId,
        animalName: selectedAnimal.name,
        animalAge: selectedAnimal.age,
        animalGender: selectedAnimal.gender
      }));
    }
  };

  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      applicant_references: [...prev.applicant_references, { name: '', phone: '', relationship: '' }]
    }));
  };

  const removeReference = (index: number) => {
    setFormData(prev => ({
      ...prev,
      applicant_references: prev.applicant_references.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Only require essential fields
    if (!formData.animalId) {
      newErrors.animalId = 'Selectarea animalului este obligatorie';
    }
    if (!formData.adopterFirstName.trim()) {
      newErrors.adopterFirstName = 'Prenumele adoptatorului este obligatoriu';
    }
    if (!formData.adopterLastName.trim()) {
      newErrors.adopterLastName = 'Numele adoptatorului este obligatoriu';
    }
    if (!formData.adopterEmail.trim()) {
      newErrors.adopterEmail = 'Email-ul este obligatoriu';
    } else if (!/\S+@\S+\.\S+/.test(formData.adopterEmail)) {
      newErrors.adopterEmail = 'Email-ul nu este valid';
    }
    if (!formData.adopterPhone.trim()) {
      newErrors.adopterPhone = 'Telefonul este obligatoriu';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const adoptionData: Partial<AdoptionApplication> = {
      animalId: formData.animalId,
      animalName: formData.animalName,
      animalAge: formData.animalAge,
      animalGender: formData.animalGender,
      adopterFirstName: formData.adopterFirstName,
      adopterLastName: formData.adopterLastName,
      adopterEmail: formData.adopterEmail,
      adopterPhone: formData.adopterPhone,
      adopterAddress: formData.adopterAddress,
      adopterCity: formData.adopterCity,
      adopterCounty: formData.adopterCounty,
      adopterPostalCode: formData.adopterPostalCode,
      housingType: formData.housingType,
      hasYard: formData.hasYard,
      yardSize: formData.yardSize,
      hasOtherPets: formData.hasOtherPets,
      otherPetsDetails: formData.otherPetsDetails,
      hasChildren: formData.hasChildren,
      childrenAges: formData.childrenAges,
      previousPetExperience: formData.previousPetExperience,
      adoptionReason: formData.adoptionReason,
      timeAvailable: formData.timeAvailable,
      applicant_references: formData.applicant_references,
      applicationDate: new Date().toISOString().split('T')[0],
      status: formData.status,
      homeVisitScheduled: formData.homeVisitScheduled,
      homeVisitDate: formData.homeVisitDate || undefined,
      contractSigned: formData.contractSigned,
      notes: formData.notes
    };

    onSave(adoptionData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === 'add' ? 'Cerere de adopție nouă' : 'Editează cererea de adopție'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Animal Selection */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Selectare animal</h3>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Animal *
                </label>
                <div className="relative">
                  {/* Search input */}
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setIsDropdownOpen(true);
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                    placeholder="Caută animal pentru adopție..."
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.animalId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  
                  {/* Currently selected animal display */}
                  {formData.animalId && !isDropdownOpen && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                      <span className="text-gray-900">
                        {formData.animalName} ({formData.animalAge} ani)
                      </span>
                    </div>
                  )}
                  
                  {/* Dropdown arrow */}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  
                  {/* Dropdown options */}
                  {isDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredAnimals.length === 0 ? (
                        <div className="px-3 py-2 text-gray-500">
                          {searchTerm ? 'Nu s-au găsit animale cu acest termen' : 'Nu există animale disponibile'}
                          <div className="text-xs text-gray-400 mt-1">
                            {!localStorage.getItem('shelter-token') ? 'Autentificare necesară' : 'Încărcare animale...'}
                          </div>
                        </div>
                      ) : (
                        filteredAnimals.map((animal) => (
                          <div
                            key={animal.id}
                            onClick={() => {
                              handleAnimalSelect(animal.id);
                              setSearchTerm(animal.name);
                              setIsDropdownOpen(false);
                            }}
                            className="px-3 py-2 hover:bg-primary-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-medium text-gray-900">{animal.name}</div>
                                <div className="text-sm text-gray-500">
                                  {animal.age} ani • {animal.gender === 'male' ? 'Mascul' : 'Femelă'} • {animal.color}
                                </div>
                              </div>
                              <div className="text-xs text-green-600 font-medium">
                                Disponibil
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {errors.animalId && <p className="text-red-500 text-xs mt-1">{errors.animalId}</p>}
              </div>
            </div>

            {/* Close dropdown when clicking outside */}
            <div 
              className={`${isDropdownOpen ? 'fixed inset-0 z-5' : 'hidden'}`}
              onClick={() => setIsDropdownOpen(false)}
            />

            {/* Adopter Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informații adoptator</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prenume *
                  </label>
                  <input
                    type="text"
                    value={formData.adopterFirstName}
                    onChange={(e) => handleInputChange('adopterFirstName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.adopterFirstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.adopterFirstName && <p className="text-red-500 text-xs mt-1">{errors.adopterFirstName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nume *
                  </label>
                  <input
                    type="text"
                    value={formData.adopterLastName}
                    onChange={(e) => handleInputChange('adopterLastName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.adopterLastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.adopterLastName && <p className="text-red-500 text-xs mt-1">{errors.adopterLastName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.adopterEmail}
                    onChange={(e) => handleInputChange('adopterEmail', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.adopterEmail ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.adopterEmail && <p className="text-red-500 text-xs mt-1">{errors.adopterEmail}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    value={formData.adopterPhone}
                    onChange={(e) => handleInputChange('adopterPhone', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.adopterPhone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.adopterPhone && <p className="text-red-500 text-xs mt-1">{errors.adopterPhone}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresa
                  </label>
                  <input
                    type="text"
                    value={formData.adopterAddress}
                    onChange={(e) => handleInputChange('adopterAddress', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Orașul
                  </label>
                  <input
                    type="text"
                    value={formData.adopterCity}
                    onChange={(e) => handleInputChange('adopterCity', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Județul
                  </label>
                  <select
                    value={formData.adopterCounty}
                    onChange={(e) => handleInputChange('adopterCounty', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Selectează județul</option>
                    {romanianCounties.map((county) => (
                      <option key={county} value={county}>
                        {county}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cod poștal
                  </label>
                  <input
                    type="text"
                    value={formData.adopterPostalCode}
                    onChange={(e) => handleInputChange('adopterPostalCode', e.target.value)}
                    placeholder="ex: 014321"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* Housing Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informații locuință</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipul locuinței
                  </label>
                  <select
                    value={formData.housingType}
                    onChange={(e) => handleInputChange('housingType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Selectează tipul</option>
                    <option value="apartment">Apartament</option>
                    <option value="house">Casă</option>
                    <option value="villa">Vilă</option>
                  </select>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hasYard"
                      checked={formData.hasYard}
                      onChange={(e) => handleInputChange('hasYard', e.target.checked)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="hasYard" className="ml-2 block text-sm text-gray-900">
                      Are curte
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hasChildren"
                      checked={formData.hasChildren}
                      onChange={(e) => handleInputChange('hasChildren', e.target.checked)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="hasChildren" className="ml-2 block text-sm text-gray-900">
                      Are copii
                    </label>
                  </div>
                </div>

                {formData.hasChildren && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vârstele copiilor
                    </label>
                    <input
                      type="text"
                      value={formData.childrenAges}
                      onChange={(e) => handleInputChange('childrenAges', e.target.value)}
                      placeholder="ex: 5, 8, 12 ani"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasOtherPets"
                    checked={formData.hasOtherPets}
                    onChange={(e) => handleInputChange('hasOtherPets', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="hasOtherPets" className="ml-2 block text-sm text-gray-900">
                    Are alte animale de companie
                  </label>
                </div>

                {formData.hasOtherPets && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Detalii despre alte animale
                    </label>
                    <textarea
                      value={formData.otherPetsDetails}
                      onChange={(e) => handleInputChange('otherPetsDetails', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Experience and Motivation */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Experiență și motivație</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Experiența anterioară cu animale
                  </label>
                  <textarea
                    value={formData.previousPetExperience}
                    onChange={(e) => handleInputChange('previousPetExperience', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivul adopției
                  </label>
                  <textarea
                    value={formData.adoptionReason}
                    onChange={(e) => handleInputChange('adoptionReason', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timpul disponibil pentru animal
                  </label>
                  <input
                    type="text"
                    value={formData.timeAvailable}
                    onChange={(e) => handleInputChange('timeAvailable', e.target.value)}
                    placeholder="ex: 2-3 ore zilnic, weekend complet"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* References - COMMENTED OUT */}
            {/* 
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Referințe</h3>
              {formData.references.map((reference, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 border border-gray-200 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nume
                    </label>
                    <input
                      type="text"
                      value={reference.name}
                      onChange={(e) => {
                        const newReferences = [...formData.references];
                        newReferences[index].name = e.target.value;
                        handleInputChange('references', newReferences);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={reference.phone}
                      onChange={(e) => {
                        const newReferences = [...formData.references];
                        newReferences[index].phone = e.target.value;
                        handleInputChange('references', newReferences);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relația
                    </label>
                    <input
                      type="text"
                      value={reference.relationship}
                      onChange={(e) => {
                        const newReferences = [...formData.references];
                        newReferences[index].relationship = e.target.value;
                        handleInputChange('references', newReferences);
                      }}
                      placeholder="ex: prieten, coleg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeReference(index)}
                      className="w-full"
                    >
                      Elimină
                    </Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addReference}>
                Adaugă referință
              </Button>
            </div>
            */}

            {/* Administrative Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informații administrative</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status cerere
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="pending">În așteptare</option>
                    <option value="approved">Aprobată</option>
                    <option value="rejected">Respinsă</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data vizitei la domiciliu
                  </label>
                  <input
                    type="date"
                    value={formData.homeVisitDate}
                    onChange={(e) => handleInputChange('homeVisitDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="md:col-span-2 flex items-center">
                  <input
                    type="checkbox"
                    id="contractSigned"
                    checked={formData.contractSigned}
                    onChange={(e) => handleInputChange('contractSigned', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="contractSigned" className="ml-2 block text-sm text-gray-900">
                    Contract semnat
                  </label>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notițe vizită la domiciliu
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button variant="outline" onClick={onClose}>
                Anulează
              </Button>
              <Button variant="primary" type="submit">
                {mode === 'add' ? 'Creează cererea' : 'Salvează modificările'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdoptionModal; 